import React from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Star, Heart, Sparkles, Scissors, PawPrint } from 'lucide-react';
import { FloatingDecor } from './FloatingDecor';
export function Hero() {
  return <section className="relative pt-16 pb-20 lg:pt-20 lg:pb-24 overflow-hidden bg-gradient-radial from-brand-cream-light via-brand-cream-light to-brand-pink-light/30" data-id="element-150">
      <div className="absolute inset-0 bg-pattern-dots opacity-30" data-id="element-151"></div>

      {/* Floating Elements */}
      <FloatingDecor Icon={Heart} className="top-20 left-[8%] text-brand-rose" delay={0} size={32} data-id="element-152" />
      <FloatingDecor Icon={PawPrint} className="top-40 left-[5%] text-brand-pink-dark" delay={1} size={24} data-id="element-153" />
      <FloatingDecor Icon={Sparkles} className="top-32 right-[10%] text-yellow-400" delay={0.5} size={28} data-id="element-154" />
      <FloatingDecor Icon={Scissors} className="bottom-20 left-[15%] text-brand-brown" delay={1.5} size={30} data-id="element-155" />
      <FloatingDecor Icon={Heart} className="bottom-32 right-[8%] text-brand-pink-dark" delay={2} size={28} data-id="element-156" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-157">
        {/* Headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-158">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-balance max-w-4xl mx-auto" data-id="element-159">
            Creá ropa{' '}
            <span className="font-script text-brand-rose text-5xl sm:text-6xl lg:text-7xl font-normal lowercase" data-id="element-160">
              adorable
            </span>{' '}
            para mascotas{' '}
            <span className="relative inline-block" data-id="element-161">
              en menos de 48h
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-brand-pink-mid opacity-70" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-162">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" data-id="element-163" />
              </svg>
            </span>
          </h1>
        </motion.div>

        {/* Product Mockup - now below the headline */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative my-8 lg:my-12 max-w-[25rem] mx-auto" data-id="element-164">
          <div className="relative z-10" data-id="element-165">
            <img src="https://cdn.magicpatterns.com/uploads/8LMGSMpzcBZ4Dp5CmzGajt/Untitled_design.png" alt="Pretty Pets PRO KIT - Moldes para ropa de mascotas" className="w-full mx-auto drop-shadow-2xl" data-id="element-166" />
          </div>

          {/* Decorative blob behind image */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-brand-pink-light/40 rounded-full blur-3xl -z-10" data-id="element-167"></div>

          {/* Floating around mockup */}
          <FloatingDecor Icon={Heart} className="top-6 right-0 text-brand-rose" delay={0.2} size={26} data-id="element-168" />
          <FloatingDecor Icon={PawPrint} className="bottom-12 left-0 text-brand-brown" delay={1.2} size={22} data-id="element-169" />
          <FloatingDecor Icon={Sparkles} className="top-12 left-4 text-yellow-400" delay={0.8} size={24} data-id="element-170" />
        </motion.div>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="text-lg sm:text-xl text-brand-brown/90 mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-171">
          Moldes profesionales listos para imprimir — incluso si nunca cosiste.
        </motion.p>

        {/* Product summary */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.35
      }} className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm md:text-base font-medium text-brand-brown mb-8" data-id="element-172">
          <span className="bg-brand-pink-light/70 px-3 py-1 rounded-full" data-id="element-173">
            📚 6 tomos + 3 bonos
          </span>
          <span className="bg-brand-pink-light/70 px-3 py-1 rounded-full" data-id="element-174">
            📄 +300 PDFs
          </span>
          <span className="bg-brand-pink-light/70 px-3 py-1 rounded-full" data-id="element-175">
            ✂️ 50+ moldes
          </span>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="flex flex-col items-center gap-4" data-id="element-176">
          <a href="#comprar" className="group relative inline-flex items-center justify-center bg-brand-rose text-white px-8 py-4 rounded-full font-bold text-lg shadow-soft-pink hover:shadow-glow-pink transition-all duration-300 hover:-translate-y-1 overflow-hidden" data-id="element-177">
            <span className="relative z-10 flex items-center gap-2" data-id="element-178">
              QUIERO MI KIT AHORA{' '}
              <span className="group-hover:translate-x-1 transition-transform" data-id="element-179">
                →
              </span>
            </span>
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-180"></div>
          </a>

          {/* Small inline trust line */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs sm:text-sm text-brand-brown/70" data-id="element-181">
            <span data-id="element-182">💳 Acceso inmediato</span>
            <span data-id="element-183">🔒 Compra 100% segura</span>
            <span data-id="element-184">✨ Garantía 7 días</span>
          </div>

          {/* Trust badges row - now BELOW the small line */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm font-medium text-brand-brown/80 mt-2 pt-4 border-t border-brand-pink-light/60 max-w-2xl" data-id="element-185">
            <span className="flex items-center gap-1.5" data-id="element-186">
              <ShieldCheck size={16} className="text-brand-rose" data-id="element-187" /> Pago seguro
            </span>
            <span className="hidden sm:inline text-brand-pink-mid" data-id="element-188">•</span>
            <span className="flex items-center gap-1.5" data-id="element-189">
              <Check size={16} className="text-brand-rose" data-id="element-190" /> Acceso inmediato
            </span>
            <span className="hidden sm:inline text-brand-pink-mid" data-id="element-191">•</span>
            <span className="flex items-center gap-1.5" data-id="element-192">
              <Star size={16} className="text-yellow-500 fill-yellow-500" data-id="element-193" />{' '}
              +12.000 mujeres felices
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}